import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ALL_NAV_ITEMS } from './navigation';

function isEditable(el) {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return el.isContentEditable === true;
}

/**
 * useGlobalShortcuts — mounts a window-level keydown listener that:
 *   - Cmd/Ctrl + K     → open command palette
 *   - /                → open command palette (when no field is focused)
 *   - g <letter>       → chord navigation (g c → /clients, g p → /partners, …)
 *
 * The `g`-chord is built dynamically from each nav item's `shortcut` field
 * so it stays in sync with navigation.js.
 */
export function useGlobalShortcuts({ onOpenPalette }) {
  const navigate = useNavigate();
  const chordRef = useRef({ active: false, timer: null });

  useEffect(() => {
    const chordMap = new Map();
    for (const item of ALL_NAV_ITEMS) {
      if (!item.shortcut) continue;
      const parts = item.shortcut.split(' ');
      if (parts[0] === 'g' && parts[1]) {
        chordMap.set(parts[1].toLowerCase(), item.path);
      }
    }

    function clearChord() {
      if (chordRef.current.timer) clearTimeout(chordRef.current.timer);
      chordRef.current = { active: false, timer: null };
    }

    function onKey(e) {
      const editable = isEditable(document.activeElement);

      // Cmd/Ctrl + K — always active (even inside inputs, standard expectation).
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenPalette?.();
        return;
      }

      if (editable) return;

      // '/' focuses the palette.
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        onOpenPalette?.();
        return;
      }

      // Chord sequence.
      if (chordRef.current.active) {
        const target = chordMap.get(e.key.toLowerCase());
        if (target) {
          e.preventDefault();
          navigate(target);
        }
        clearChord();
        return;
      }

      if (e.key === 'g' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        chordRef.current.active = true;
        chordRef.current.timer = setTimeout(clearChord, 1000);
      }
    }

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearChord();
    };
  }, [navigate, onOpenPalette]);
}

export default useGlobalShortcuts;
